import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type FC, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { type AgentDetailsImage } from './agent-details-content';

const ICON_STROKE_WIDTH = 1.75;

const SLIDE_MS = 450;

/**
 * `transitionend` does not always arrive - none runs under reduced motion, in
 * a hidden tab, or in a test environment - and without it the track would stay
 * on a clone. Same guard as `add-context-attachments.tsx`.
 */
const SETTLE_FALLBACK_MS = SLIDE_MS + 50;

/*
 * The frame's round 36px button, 12px in from the image's edge (Figma
 * 11263:75345). Hidden until the pointer is over the image: it fades in and
 * slides 4px in from its edge. Keyboard focus anywhere on the image shows both,
 * and a touch screen, which has no hover, always shows them.
 */
const ARROW =
  'absolute top-1/2 z-10 flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-[var(--ds-color-bg-primary)] text-[var(--ds-color-text-primary)] opacity-0 shadow-[0_2px_4px_rgba(0,0,0,0.12)] transition-[opacity,translate,scale] duration-200 ease-out group-hover/stage:translate-x-0 group-hover/stage:opacity-100 group-focus-within/stage:translate-x-0 group-focus-within/stage:opacity-100 pointer-coarse:translate-x-0 pointer-coarse:opacity-100 hover:scale-105 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ds-color-border-secondary)] motion-reduce:transition-none';

/** Four thumbnails fill the 800px column with three 16px gaps, as drawn. */
const THUMBNAIL_WIDTH = 'w-[calc((100%-3*var(--ds-space-4))/4)]';

interface AgentDetailsGalleryProps {
  images: AgentDetailsImage[];
}

const Picture: FC<{ image: AgentDetailsImage; className?: string }> = ({ image, className }) =>
  image.src === undefined ? (
    <span
      aria-hidden="true"
      className={`block size-full bg-[var(--ds-color-bg-brand-light)] ${className ?? ''}`}
    />
  ) : (
    <img
      alt={image.alt}
      className={`block size-full object-cover ${className ?? ''}`}
      draggable={false}
      src={image.src}
    />
  );

/** A copy at one end of the track, so the wrap can slide on. Hidden, and unnamed. */
const Clone: FC<{ image: AgentDetailsImage }> = ({ image }) => (
  <div aria-hidden="true" className="size-full shrink-0">
    <Picture image={{ ...image, alt: '' }} />
  </div>
);

/**
 * One large 16:9 image over a row of thumbnails, Figma 11914:189023, with the
 * previous and next buttons over the image, Figma 11263:75345. The arrows wrap
 * at both ends, and carry on in the same direction rather than rewinding: the
 * track holds a clone of the last image before the first and of the first
 * after the last, and once a step lands on a clone it snaps, unanimated, to the
 * image the clone stands in for.
 *
 * Motion: the large image slides to the picked one on a track, the other
 * thumbnails fade back, and a thumbnail zooms a little on hover. Reduced motion
 * turns all three off.
 */
export const AgentDetailsGallery: FC<AgentDetailsGalleryProps> = ({ images }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  // Where the track rests: image `i` sits at `i + 1`, the clones at 0 and
  // count + 1. The ref mirrors it for the fallback timer and back-to-back steps.
  const [offset, setOffset] = useState(1);
  const offsetRef = useRef(1);
  const [animated, setAnimated] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);
  const settleTimer = useRef<number | undefined>(undefined);
  const rowRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const count = images.length;

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  // More than four thumbnails scroll, so a pick by arrow can land off-screen.
  // Only the row scrolls: scrollIntoView would also move the modal's body.
  useEffect(() => {
    const row = rowRef.current;
    const thumbnail = thumbnailRefs.current[current];
    if (row === null || thumbnail === null || thumbnail === undefined) return;

    const start = thumbnail.offsetLeft;
    const end = start + thumbnail.offsetWidth;
    if (start < row.scrollLeft) row.scrollLeft = start;
    else if (end > row.scrollLeft + row.clientWidth) row.scrollLeft = end - row.clientWidth;
  }, [current]);

  if (count === 0) return null;

  const onClone = (at: number) => at === 0 || at === count + 1;

  /**
   * Off a clone, onto the image it stands in for, with no animation: the jump
   * commits with the transition off, the reflow makes the browser take that
   * position, and only then does the transition come back.
   */
  const settle = () => {
    window.clearTimeout(settleTimer.current);
    const from = offsetRef.current;
    if (!onClone(from)) return;

    const to = from === 0 ? count : 1;
    offsetRef.current = to;
    flushSync(() => {
      setAnimated(false);
      setOffset(to);
    });
    void trackRef.current?.offsetWidth;
    setAnimated(true);
  };

  const moveTo = (to: number) => {
    offsetRef.current = to;
    setOffset(to);
    if (onClone(to)) settleTimer.current = window.setTimeout(settle, SETTLE_FALLBACK_MS);
  };

  // A step that comes while the track is still on a clone settles it first, so
  // a quick second press at the wrap still counts.
  const step = (delta: number) => {
    settle();
    moveTo(offsetRef.current + delta);
    setCurrent((index) => (index + delta + count) % count);
  };

  const pick = (index: number) => {
    settle();
    moveTo(index + 1);
    setCurrent(index);
  };

  const position = (index: number) =>
    t('agents-directory.details.image-position', { index: index + 1, count });

  return (
    <section
      aria-label={t('agents-directory.details.gallery')}
      aria-roledescription="carousel"
      className="flex flex-col gap-4"
    >
      <div className="group/stage relative aspect-video overflow-hidden rounded-[var(--ds-radius-8)]">
        {/* The track: every slide side by side, moved one width per step,
            between the two clones when there is more than one image. */}
        <div
          aria-live="polite"
          className={`flex size-full will-change-transform ${
            animated
              ? 'transition-transform ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'
              : ''
          }`}
          onTransitionEnd={(event) => {
            if (event.currentTarget === event.target && event.propertyName === 'transform') {
              settle();
            }
          }}
          ref={trackRef}
          style={{
            transform: `translateX(-${(count > 1 ? offset : 0) * 100}%)`,
            transitionDuration: animated ? `${SLIDE_MS}ms` : undefined,
          }}
        >
          {count > 1 && <Clone image={images[count - 1]!} key="clone-start" />}
          {images.map((image, index) => (
            <div
              aria-hidden={index === current ? undefined : true}
              aria-label={position(index)}
              aria-roledescription="slide"
              className="size-full shrink-0"
              key={`${image.src ?? ''}${image.alt}`}
              role="group"
            >
              <Picture image={image} />
            </div>
          ))}
          {count > 1 && <Clone image={images[0]!} key="clone-end" />}
        </div>
        {count > 1 && (
          <>
            <button
              aria-label={t('agents-directory.details.previous-image')}
              className={`${ARROW} left-3 -translate-x-1`}
              onClick={() => step(-1)}
              type="button"
            >
              <ChevronLeft
                aria-hidden="true"
                className="size-[18px]"
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
            <button
              aria-label={t('agents-directory.details.next-image')}
              className={`${ARROW} right-3 translate-x-1`}
              onClick={() => step(1)}
              type="button"
            >
              <ChevronRight
                aria-hidden="true"
                className="size-[18px]"
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
          </>
        )}
      </div>

      {count > 1 && (
        <>
          {/* p-1 -m-1 so the focus ring of an edge thumbnail is not clipped by
              the scroller. `relative` makes the row the thumbnails'
              offsetParent. */}
          <div
            className="relative -m-1 flex gap-4 overflow-x-auto scroll-smooth p-1 [scrollbar-width:none] motion-reduce:scroll-auto"
            ref={rowRef}
          >
            {images.map((thumbnail, index) => (
              <button
                aria-current={index === current ? 'true' : undefined}
                aria-label={position(index)}
                className={`group relative aspect-video shrink-0 cursor-pointer overflow-hidden rounded-[var(--ds-radius-8)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ds-color-border-secondary)] ${THUMBNAIL_WIDTH}`}
                key={`${thumbnail.src ?? ''}${thumbnail.alt}`}
                onClick={() => pick(index)}
                ref={(element) => {
                  thumbnailRefs.current[index] = element;
                }}
                type="button"
              >
                {/* Named by the button, so the picture itself is decorative. The
                    frame draws no selected state. Dimming the others marks the
                    picked one on any screenshot, including ones framed in brand
                    green, where a green ring would not show. */}
                <Picture
                  className={`transition-[transform,opacity] duration-200 ease-out group-hover:scale-[1.04] group-hover:opacity-100 motion-reduce:transition-none ${
                    index === current ? 'opacity-100' : 'opacity-50'
                  }`}
                  image={{ ...thumbnail, alt: '' }}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
};
